<script setup lang="ts">
import { useSetFormValues, useSubmitForm } from 'vee-validate'
import type { MaybeRef } from 'vue'
import { provideAutosave } from './context'
import type { Elemental } from '~~/src/types/elemental'
import { useElementalFormStore } from '~~/src/stores/elementalForm'

interface Props {
  autoSaveId?: MaybeRef<string>
  isSaving?: Ref<boolean>
  delayToSave?: number
}
interface Emits {
  (event: 'submit', values: AditonalFormData & Elemental): void
}

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

const saveDelay = props.delayToSave ?? 300

defineOptions({
  name: 'FormAutomaticSave',
})

const store = useElementalFormStore()

const changesMap = reactive(new Map<string, unknown>())
const fieldListeners = reactive(new Map<string, Set< (value: unknown) => void>>())
const lastChangeStamp = ref<number | null>(null)
const lastSaveStamp = ref<number | null>(null)

const id = computed(() => unref(props.autoSaveId) ?? randomID())
const changes = computed(() => changesMap.size)

const submit = useSubmitForm((values) => {
  const data = values as unknown as AditonalFormData & Elemental
  emits('submit', data)
})
const setFieldValue = useSetFormValues()
const debounceFuntion = useDebounceFn(() => {
  lastSaveStamp.value = Date.now()
  changesMap.clear()
  submit()
}, saveDelay)

useCustomEventListener('form:auto-save:change', (event) => {
  // eslint-disable-next-line eqeqeq
  if (event.id == id.value) {
    setFieldValue({
      [event.field]: event.value,
    })
    dispatchFieldListeners(event.field, event.value)
  }
})

function handlerUpdateCellRow<T>(field: string, value: T) {
  if (field !== 'template')
    return

  const currentElemental = store.data

  if (!currentElemental || !currentElemental!.colId || !currentElemental!.rowId)
    return

  useDispatchCustomEvent('table:update-row-data', {
    colId: currentElemental.colId,
    rowId: currentElemental.rowId,
    cellId: currentElemental.id!,
    value: value as string,
  })
}

function handlerUpdateColumn<T>(field: string, value: T) {
  const currentElemental = store.data

  if (!currentElemental || !currentElemental!.tableId)
    return
  if (field !== 'title')
    return

  useDispatchCustomEvent('table:update-column-data', {
    colId: currentElemental.id!,
    value: value as string,
  })
}

function onValueChange<T>(field: string, value: T) {
  changesMap.set(field, value)
  lastChangeStamp.value = Date.now()
  if (props.autoSaveId) {
    useDispatchCustomEvent('form:auto-save:changed', {
      id: id.value,
      field,
      value,
    })
  }
  handlerUpdateCellRow(field, value)
  handlerUpdateColumn(field, value)
  debounceFuntion()
}
onUnmounted(() => {
  if (changesMap.size > 0)
    submit()

  fieldListeners.clear()
  changesMap.clear()
})

function dispatchFieldListeners(field: string, value: unknown) {
  if (!fieldListeners.has(field))
    return

  fieldListeners.get(field)?.forEach((listener) => {
    listener(value)
  })
}

const addFieldListener = (field: string, listener: (value: unknown) => void) => {
  if (!fieldListeners.has(field))
    fieldListeners.set(field, new Set())

  fieldListeners.get(field)?.add(listener)
  return () => {
    fieldListeners.get(field)?.delete(listener)
  }
}

provideAutosave({
  injected: true,
  changes,
  lastChangeStamp,
  lastSaveStamp,
  isSaving: props.isSaving ?? false,
  onValueChange,
  addFieldListener,
})
</script>

<template>
  <div>
    <slot />
  </div>
</template>
